import React, { useEffect, useState } from "react";
import "./TaskCenter.css";
import { useConstants } from "../../constant/constantProvider";
import { useDispatch } from "react-redux";
import { fetchTranslateHistory, fetchTranslatedFile } from "../../store/apiActions";

// components
import Modal from "../Modal/Modal";
import { TranslateFilePreview } from "../TranslateFilePreview/TranslateFilePreview";
import { getObjectUrlFromBase64, getFileExtensionFromPath, getFileNameFromPath } from "../../utils/fileHelper";
import { useAuth } from "../../hooks/useAuth";

// assets
import arrowBack from "../../assets/images/left-black-icon.svg";
import failedIcon from "../../assets/images/red-info.svg";
import CLOSE_ICON from "../../assets/images/previewClose.svg"

const TaskCenter = ({ onClose }) => {
  const dispatch = useDispatch();
  const { TASK_CENTER, UPLOAD_TIME, COMPLETED, FAILED, FILE_NAME, TRANSLATED_TIME, STATUS, ACTION, PREVIEW, CANCEL, NO_DATA_FOUND } = useConstants();

  const [tasks, setTasks] = useState([]);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [originalFile, setOriginalFile] = useState(null);
  const [translatedFile, setTranslatedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { username } = useAuth();

  const handleAction = async (action, task) => {
    if (action === "preview") {
      setPreviewLoading(true);
      setOpenPreviewModal(true);

      const requestResponse = await dispatch(
        fetchTranslatedFile({ file_path: task?.request_path })
      );

      const requestExt = getFileExtensionFromPath(task?.request_path);
      const requestBlobUrl = getObjectUrlFromBase64(
        requestResponse?.encoded_filebytes,
        requestExt
      );

      const translatedResponse = await dispatch(
        fetchTranslatedFile({ file_path: task?.response_path })
      );

      const responseExt = getFileExtensionFromPath(task?.response_path);
      const responseBlobUrl = getObjectUrlFromBase64(
        translatedResponse?.encoded_filebytes,
        responseExt
      );

      const requestFile = {
        url: requestBlobUrl,
        type: requestExt,
        name: getFileNameFromPath(task?.request_path),
      };

      const responseFile = {
        url: responseBlobUrl,
        type: responseExt,
        name: getFileNameFromPath(task?.response_path),
      };

      setOriginalFile(requestFile);
      setTranslatedFile(responseFile);
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const payload = { user_id: username, count_translation: 20 };
      const response = await dispatch(fetchTranslateHistory(payload));

      if (response) {
        setTasks(response?.tasks || []);
      }
      setLoading(false);
    };

    fetchData();

    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <div className="task-center">
      <div className="task-center-header">
        {onClose && (
          <button className="button-style back-button" type="button" onClick={onClose}>
            <img src={arrowBack} alt="back" />
          </button>
        )}
        <div className="task-center-title">{TASK_CENTER}</div>
      </div>

      {loading &&
        <div className="loader-task-container">
          <div className="loader spinner-loader"></div>
          <p>Loading ..</p>
        </div>
      }

      {!loading &&
        <div className="task-table-wrapper">
          {tasks.length === 0 ? (
            <div className="no-data-message">
              {NO_DATA_FOUND}
            </div>
          ) : (
            <table className="task-table">
              <thead>
                <tr>
                  <th>{FILE_NAME}</th>
                  <th>{UPLOAD_TIME}</th>
                  <th>{TRANSLATED_TIME}</th>
                  <th>{STATUS}</th>
                  <th>{ACTION}</th>
                </tr>
              </thead>
              <tbody>
                {tasks?.map((task, index) => (
                  <tr key={index}>
                    <td className="file-name">{task.filename || "N/A"}</td>
                    <td>{task.created_at}</td>
                    <td>{task.translated_at || "--"}</td>
                    <td className="task-status">
                      {task.status === FAILED ? (
                        <div className="status-failed tooltip-wrapper">
                          {task.status}
                          <img
                            src={failedIcon}
                            alt="failed"
                            className="failed-icon"
                          />
                          <span className="tooltip-text">
                            {task.error || "Translation failed"}
                          </span>
                        </div>
                      ) : (
                        task.status
                      )}
                    </td>
                    <td className="task-action">
                      {task.status === COMPLETED && (
                        <button
                          className="button-style action-btn preview"
                          type="button"
                          onClick={() => handleAction("preview", task)}
                        >
                          {PREVIEW}
                        </button>
                      )}
                      {task.status.includes(COMPLETED) &&
                        !task.status.startsWith(COMPLETED) && (
                          <button
                            className="button-style action-btn cancel"
                            type="button"
                            onClick={() => handleAction("cancel", task)}
                          >
                            {CANCEL}
                          </button>
                        )}
                      {task.status === FAILED && (
                        <div className="action-btn retry">{FAILED}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      }
      {openPreviewModal && (
        <DocumentPreviewModal
          originalFile={originalFile}
          translatedFile={translatedFile}
          handleCloseModal={() => {
            setOpenPreviewModal(false);
            setPreviewLoading(false);
          }}
          isLoading={previewLoading}
        />
      )}
    </div>
  );
};

export default TaskCenter;

export const DocumentPreviewModal = ({ originalFile, translatedFile, handleCloseModal, isLoading }) => {
  return (
    <Modal open={true} onClose={handleCloseModal}>
      <div className="document-preview-main-container">
        <div className="document-header-section">
          <div className="heading">Document Preview</div>
          <button className="button-style close" onClick={handleCloseModal}>
            <img src={CLOSE_ICON} alt="close" />
          </button>
        </div>

        <div className="preview-body">
          <TranslateFilePreview fileURL={originalFile?.url} fileType={originalFile?.type} fileName={originalFile?.name} enabledActionButton={false} isConversion={true} isLoading={isLoading} />
          <TranslateFilePreview fileURL={translatedFile?.url} fileType={translatedFile?.type} fileName={translatedFile?.name} isConversion={true} isLoading={isLoading} />
        </div>
      </div>
    </Modal>
  );
};
